/*
 * The answer to "Was it wrong, or did it change?" a queued change carries.
 * A real change may name the day it happened, when somebody said so with no
 * visit open. A correction has no day: the record was never right. The
 * outboxes collapse edits to one record, and two edits answered differently,
 * or dated differently, stay two statements.
 */

/**
 * A correction has no day: the record was never right, so nothing changed
 * on one. A real change may name the day, or leave it out and be dated the
 * day it is saved.
 */
export type ChangeAnswer =
  | { isCorrection: true }
  | { isCorrection: false; changedOn: string | undefined }

interface Answerable {
  isCorrection?: boolean | undefined
  changedOn?: string | undefined
}

export function answerOf(operation: Answerable): ChangeAnswer | undefined {
  if (operation.isCorrection === undefined) return undefined
  if (operation.isCorrection) return { isCorrection: true }
  return { isCorrection: false, changedOn: operation.changedOn }
}

export function sameAnswer(a: ChangeAnswer, b: ChangeAnswer): boolean {
  if (a.isCorrection && b.isCorrection) return true
  if (!a.isCorrection && !b.isCorrection) {
    return a.changedOn === b.changedOn
  }
  return false
}

/** The operation carrying this answer. */
export function withAnswer<T extends Answerable>(
  operation: T,
  answer: ChangeAnswer,
): T {
  if (answer.isCorrection) {
    return { ...operation, isCorrection: true }
  }
  return {
    ...operation,
    isCorrection: false,
    ...(answer.changedOn === undefined ? {} : { changedOn: answer.changedOn }),
  }
}
